import React, { useState } from 'react';
import '../../../styles/resume-editor.css';

interface AddSectionProps {
  onBack: () => void;
  onAddSection: (sectionTitle: string, layout: 'inline' | 'stacked') => void;
}

type LayoutType = 'inline' | 'stacked';

export const AddSection: React.FC<AddSectionProps> = ({ onBack, onAddSection }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [customSectionTitle, setCustomSectionTitle] = useState('');
  const [selectedLayout, setSelectedLayout] = useState<LayoutType>('stacked');

  const popularSections = [
    'Interests',
    'Key Achievements',
    'Awards',
    'References',
    'Additional Information',
    'Publications',
    'Hobbies',
    'Courses',
  ];

  const handleAddPopularSection = (sectionTitle: string) => {
    setSelectedSection(sectionTitle);
    setStep(2);
  };

  const handleAddCustomSection = () => {
    if (customSectionTitle.trim()) {
      setSelectedSection(customSectionTitle.trim());
      setStep(2);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddCustomSection();
    }
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setSelectedSection('');
  };

  const handleCreateSection = () => {
    onAddSection(selectedSection, selectedLayout);
  };

  if (step === 1) {
    return (
      <div className="resume-editor__section-expanded-body">
        <div className="add-section__popular">
          <h3 className="add-section__popular-title">Other Popular Sections</h3>
          <div className="add-section__popular-list">
            {popularSections.map((section) => (
              <button
                key={section}
                className="add-section__popular-item"
                onClick={() => handleAddPopularSection(section)}
              >
                {section}
              </button>
            ))}
          </div>
        </div>

        <div className="add-section__separator" />

        <div className="add-section__custom">
          <div className="add-section__custom-label">or create your own</div>
          <div className="add-section__custom-input-wrapper">
            <input
              type="text"
              className="add-section__custom-input"
              placeholder="Section Title"
              value={customSectionTitle}
              onChange={(e) => setCustomSectionTitle(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button
              className="add-section__custom-submit"
              onClick={handleAddCustomSection}
              disabled={!customSectionTitle.trim()}
            >
              →
            </button>
          </div>
        </div>

        <div className="add-section__footer">
          <button
            type="button"
            className="add-section__back-btn"
            onClick={onBack}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="resume-editor__section-expanded-body">
      <div className="add-section__layout-header">
        <h3 className="add-section__layout-title">Choose Layout</h3>
        <p className="add-section__layout-subtitle">Defines how your section entries are displayed.</p>
      </div>

      <div className="add-section__layout-options">
        <div
          className={`add-section__layout-card ${selectedLayout === 'inline' ? 'add-section__layout-card--selected' : ''}`}
          onClick={() => setSelectedLayout('inline')}
        >
          <div className="add-section__layout-content">
            <h4 className="add-section__layout-name">Inline</h4>
            <p className="add-section__layout-description">
              Ideal for simple lists or detailed entries where you need a compact, space-saving layout.
            </p>
            <div className="add-section__layout-suggested">
              <span className="add-section__layout-suggested-label">Suggested for</span>
              <div className="add-section__layout-tags">
                <span className="add-section__layout-tag">Hobbies</span>
                <span className="add-section__layout-tag">Key Achievements</span>
                <span className="add-section__layout-tag">References</span>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`add-section__layout-card ${selectedLayout === 'stacked' ? 'add-section__layout-card--selected' : ''}`}
          onClick={() => setSelectedLayout('stacked')}
        >
          <div className="add-section__layout-content">
            <h4 className="add-section__layout-name">Stacked</h4>
            <p className="add-section__layout-description">
              Ideal for an expansive, detailed layout, giving each entry its own block to showcase more complex information (title, date, descriptions).
            </p>
            <div className="add-section__layout-suggested">
              <span className="add-section__layout-suggested-label">Suggested for</span>
              <div className="add-section__layout-tags">
                <span className="add-section__layout-tag">Publications</span>
                <span className="add-section__layout-tag">Awards</span>
                <span className="add-section__layout-tag">Other Experience</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="add-section__footer">
        <button
          type="button"
          className="add-section__btn add-section__btn--cancel"
          onClick={handleBackToStep1}
        >
          Back
        </button>
        <button
          type="button"
          className="add-section__btn add-section__btn--create"
          onClick={handleCreateSection}
        >
          Create Section
        </button>
      </div>
    </div>
  );
};

